/**
 * Splits combined "Yield stress, Fy … ; Ultimate stress, Fu …" copy into two rows
 * and exposes values for dimensions tables (defaults / legacy role rows).
 */

const COMBINED_STRESS_RE =
  /yield\s*stress,?\s*fy\s*([\d.]+)\s*m\s*pa\s*[;|]\s*ultimate\s*stress,?\s*fu\s*([\d.]+)\s*m\s*pa/i;

/**
 * @param {string} text — label, value, or both concatenated
 * @returns {{ fyDisplay: string, fuDisplay: string } | null}
 */
export function parseCombinedYieldUltimateStress(text) {
  const raw = String(text || "").trim();
  if (!raw) return null;
  const m = raw.match(COMBINED_STRESS_RE);
  if (!m) return null;
  return { fyDisplay: `${m[1]} MPa`, fuDisplay: `${m[2]} MPa` };
}

function rowTextBlob(row) {
  const label = String(row?.label ?? "").trim();
  const value = String(row?.value ?? "").trim();
  if (label && value) return `${label} ${value}`;
  return label || value;
}

/**
 * @param {Array<{ label?: string, value?: string, unit?: string, reference?: string }>} rows
 * @returns {Array<typeof rows[0]>}
 */
export function expandMaterialPropertyRows(rows) {
  return (rows || []).flatMap((row) => {
    const parsed = parseCombinedYieldUltimateStress(rowTextBlob(row));
    if (!parsed) return [row];
    const ref = row?.reference || row?.standard || row?.astm || "";
    return [
      { ...row, label: "Yield stress, Fy", value: parsed.fyDisplay, unit: "", reference: ref },
      { ...row, label: "Ultimate stress, Fu", value: parsed.fuDisplay, unit: "", reference: ref },
    ];
  });
}

/**
 * Keys merged into section dimension defaults (dynamic dimensions table).
 * @param {Array<{ label?: string, value?: string }>} materialPropertyRows — raw rows before expand
 */
export function stressDefaultsForSectionDimensions(materialPropertyRows) {
  for (const row of materialPropertyRows || []) {
    const parsed = parseCombinedYieldUltimateStress(rowTextBlob(row));
    if (parsed) {
      return {
        "Yield stress, Fy": parsed.fyDisplay,
        "Ultimate stress, Fu": parsed.fuDisplay,
      };
    }
  }
  return {};
}

/**
 * Extra rows for legacy accessory dimensions table (Role / H1 / …).
 * @param {Array<{ label?: string, value?: string }>} materialPropertyRows
 */
export function legacyStressDimensionRows(materialPropertyRows) {
  for (const row of materialPropertyRows || []) {
    const parsed = parseCombinedYieldUltimateStress(rowTextBlob(row));
    if (parsed) {
      return [
        {
          role: "Yield stress, Fy",
          h1: parsed.fyDisplay,
          width: "",
          thickness: "",
          length: "",
          zincCoating: "",
        },
        {
          role: "Ultimate stress, Fu",
          h1: parsed.fuDisplay,
          width: "",
          thickness: "",
          length: "",
          zincCoating: "",
        },
      ];
    }
  }
  return [];
}
